import { Status, Wrapper } from '@googlemaps/react-wrapper';
import type { TFunction } from 'i18next';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Modal, ModalActions, ModalContent, ModalTitle } from '@ifs-gcp/components';
import type { ITemplateNode } from '@ifs-gcp/core';
import type { CompanyGpsCoordinatesChild, Language, QuestionChild } from '@ifs-gcp/core/models';

import { useAuditStore } from '@/store/auditStore';
import { useTouchedFieldsStore } from '@/store/validationStore';
import { getTranslation } from '@/utils/translations';

import { GpsMap } from '../gps-map';
import type { CountryList, GpsModalSteps } from '../types';
import {
  type GeoLocation,
  formatLocationParam,
  getAnswerString,
  getInitialLocation,
} from '../utils';
import styles from './gps-modal.module.scss';

type InfoTextContentProps = {
  text: string;
};

const InfoTextContent = ({ text }: InfoTextContentProps) => {
  return (
    <div className={styles.infoTextContainer}>
      <p>{text}</p>
    </div>
  );
};

type GpsModalProps = {
  element: ITemplateNode<CompanyGpsCoordinatesChild>;
  countryList: CountryList;
  language: Language;
  companyGpsLatitudeField: QuestionChild;
  companyGpsLongitudeField: QuestionChild;
  tosConfirmed: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTosConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
};

const mapRender = (t: TFunction) => {
  const MapRenderComponent = (status: Status) => {
    if (status === Status.LOADING) {
      return <InfoTextContent text={t('loading')} />;
    }

    return <InfoTextContent text={t('something_went_wrong')} />;
  };

  MapRenderComponent.displayName = 'MapRenderComponent';
  return MapRenderComponent;
};

export const GpsModal = ({
  element,
  countryList,
  language,
  companyGpsLatitudeField,
  companyGpsLongitudeField,
  tosConfirmed,
  setIsModalOpen,
  setTosConfirmed,
}: GpsModalProps) => {
  const {
    props: { widgetTitle, cancelButtonText, confirmButtonText, selectButtonText },
  } = element;

  const { t } = useTranslation();
  const { touchField } = useTouchedFieldsStore();

  const audit = useAuditStore((state) => state.audit)!;

  const [location, setLocation] = useState<GeoLocation | null>(null);
  const [step, setStep] = useState<GpsModalSteps>(tosConfirmed ? 'map' : 'confirmation');

  const moduleIds = Array.from(audit.modules.modules.keys());
  const availableModuleId = moduleIds.find((moduleId) =>
    companyGpsLatitudeField.props.context.includes(moduleId),
  ) as string;

  const moduleQuestions = audit.modules.getById(availableModuleId)?.questions.toSerialized() || {};

  const companyGpsLatitude = getAnswerString(moduleQuestions, 'companyGpsLatitude');

  const companyGpsLongitude = getAnswerString(moduleQuestions, 'companyGpsLongitude');

  const getCompanyAddress = () => {
    let companyCountry = getAnswerString(moduleQuestions, 'companyCountry');

    if (companyCountry) {
      const country = countryList.find((c) => c.value === companyCountry);
      companyCountry = country?.text?.[language] || '';
    }

    const addressKeys = ['companyStreetNo', 'companyZip', 'companyCity'];

    return addressKeys
      .map((key) => getAnswerString(moduleQuestions, key))
      .concat(companyCountry)
      .filter(Boolean)
      .join(', ');
  };

  const companyAddress = getCompanyAddress();

  const memoizedGetInitialLocation = useCallback(
    () =>
      getInitialLocation({
        companyGpsLatitude,
        companyGpsLongitude,
        companyAddress,
      }),
    [companyAddress, companyGpsLatitude, companyGpsLongitude],
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleNext = () => {
    if (step === 'confirmation') {
      setStep('map');
      setTosConfirmed(true);
    } else if (location) {
      audit
        .factory('question', companyGpsLatitudeField)
        .save(formatLocationParam(location.latitude));

      audit
        .factory('question', companyGpsLongitudeField)
        .save(formatLocationParam(location.longitude));

      [companyGpsLatitudeField, companyGpsLongitudeField].forEach((questionTemplate) => {
        touchField(questionTemplate.props.id, questionTemplate.props.context);
      });

      handleModalClose();
    }
  };

  const steps: Record<GpsModalSteps, React.ReactNode> = {
    confirmation: (
      <div className={styles.confirmationScreen}>
        <Trans
          i18nKey="audit.gps_modal.confirmation_note"
          components={{
            a: (
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                Privacy Policy – Privacy & Terms – Google
              </a>
            ),
          }}
        />
      </div>
    ),
    map: (
      <Wrapper
        apiKey={import.meta.env.VITE_API_KEY_MAPS}
        render={mapRender(t)}
        libraries={['maps', 'places', 'geocoding']}
        language={language}
      >
        <GpsMap
          location={location}
          setLocation={setLocation}
          getInitialLocation={memoizedGetInitialLocation}
        />
      </Wrapper>
    ),
  };

  const isMap = step === 'map';

  return (
    <Modal
      open
      onClose={handleModalClose}
      className={`${styles.modal} ${isMap ? styles.wide : ''}`}
      classes={{ container: styles.container }}
    >
      <ModalTitle>{getTranslation(widgetTitle)}</ModalTitle>

      <ModalContent className={styles.content} classes={{ root: styles.contentRoot }}>
        {steps[step]}
      </ModalContent>

      <ModalActions>
        <Button onClick={handleModalClose} color="secondary">
          {getTranslation(cancelButtonText)}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          type="button"
          onClick={handleNext}
          disabled={isMap && !location}
        >
          {getTranslation(isMap ? selectButtonText : confirmButtonText)}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default GpsModal;
